import { Location } from '@angular/common';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { AlertButton, AlertController, LoadingController, MenuController, ModalController, NavController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { AppComponent } from 'src/app/app.component';
import { HttpService } from '../data/service/http.service';
import { AuthenticationService } from '../modules/auth-core/data/service/authentication.service';

export enum SavePromptResponse {
    Cancel = 'cancel',
    No = 'no',
    Yes = 'yes'
}

export class ServiceHelper {

    static authSvc: AuthenticationService;
    static navCtrl: NavController;
    static platform: Platform;
    static popoverCtrl: PopoverController;
    static loadCtrl: LoadingController;
    static alertCtrl: AlertController;
    static toastCtrl: ToastController;
    static httpSvc: HttpService;
    static title: Title;
    static menuCtrl: MenuController;
    static modalCtrl: ModalController;
    static sanitizer: DomSanitizer;
    static location: Location;
    static appComponent: AppComponent;

    static setLocation(url:string){
        ServiceHelper.location.go(url);
    }

    static async displayAlert(title: string, message: string, buttons:(string | AlertButton)[] = ['OK']): Promise<HTMLIonAlertElement> {
        const alert = await ServiceHelper.alertCtrl.create({
            header: title,
            message,
            buttons: buttons
        });
        await alert.present();
        return alert;
    }
    
    static async displayLoad( message: string) {
        const load = await ServiceHelper.loadCtrl.create({
            message: message
          });
          load.present();
        return load;
    }
    
    static async displayToast(message: string, duration: number, position: 'top' | 'bottom' | 'middle' = 'top') {
        const toast = await ServiceHelper.toastCtrl.create({ message, position, duration });
        await toast?.present();
        return toast;
    }

    static async confirmSomething(title: string, body: string, cancelLbl: string = 'Cancel', confirmLbl: string = 'Confirm', confirmId: string = null): Promise<boolean> {
        return new Promise(async (resolve) => {
            // create modal alert
            const confirmationAlert = await this.alertCtrl?.create({
                header: title,
                message: body,
                buttons: [
                    {
                        text: cancelLbl,
                        role: 'cancel',
                        handler: () => resolve(false)
                    },
                    {
                        text: confirmLbl,
                        id: confirmId,
                        handler: () => resolve(true)
                    }
                ]
            });
            confirmationAlert?.present();
        });
    }

    static async promptForSaveSomething(title: string, body: string) {
        return new Promise(async (resolve) => {
            const confirm = await ServiceHelper.alertCtrl.create({
                header: title,
                message: body,
                buttons: [
                    {
                        text: 'Cancel',
                        handler: () => {
                            resolve(SavePromptResponse.Cancel);
                        }
                    }, {
                        text: 'No',
                        handler: () => {
                            confirm.dismiss().then(() => {
                                resolve(SavePromptResponse.No);
                            });
                        }
                    }, {
                        text: 'Yes',
                        handler: () => {
                            confirm.dismiss().then(async () => {
                                resolve(SavePromptResponse.Yes);
                            });
                        }
                    }
                ]
            });
            confirm.present();
        });
    }

    static async getValue(title: string, body: string, cancelLbl: string = 'Cancel', confirmLbl: string = 'Confirm') {
        return new Promise(async (resolve) => {
            // create modal alert
            const confirmationAlert = await this.alertCtrl?.create({
                header: title,
                message: body,
                buttons: [
                    {
                        text: cancelLbl,
                        role: 'cancel',
                        handler: () => resolve(null)
                    },
                    {
                        text: confirmLbl,
                        handler: (data) => resolve(data)
                    }
                ],
                inputs: [
                    {
                        name: 'value',
                        type: 'text',
                        placeholder: 'Value'
                    }
                ]
            });
            confirmationAlert?.present();
        });
    }


    // This function matches how the C# Math.Round method works,
    // which is to round to the nearest even number.
    static roundToEven(value: number, decimals = 2): number {
        // First multiply the value by 10^decimals to get the value as an integer
        const factor = Math.pow(10, decimals);
        const scaled = value * factor;
    
        // Then get the integer part of the value
        const integer = Math.floor(scaled);
        // Then get the fractional part of the value
        const fraction = scaled - integer;
    
        // Treat values extremely close to .5 as an exact midpoint
        const epsilon = 1e-10;
    
        // If the fractional part is extremely close to .5, round to the nearest even number
        if (Math.abs(fraction - 0.5) < epsilon) {
            return (integer % 2 === 0 ? integer : integer + 1) / factor;
        }
    
        // Otherwise, round to the nearest integer and divide by 10^decimals
        return Math.round(scaled) / factor;
    }
}
